import { ConfigProvider } from "antd";
import zhCN from "antd/locale/zh_CN";
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { FONT_SCALE_ZOOM, makeAntdTheme } from "./tokens";
import type { ColorMode, FontScale } from "./tokens";

type ThemeCtx = {
  mode: ColorMode;
  setMode: (m: ColorMode) => void;
  toggle: () => void;
  fontScale: FontScale;
  setFontScale: (s: FontScale) => void;
};

const Ctx = createContext<ThemeCtx | null>(null);
const STORAGE_KEY = "priomail.colorMode";
const FONT_KEY = "priomail.fontScale";

function readInitialMode(): ColorMode {
  if (typeof window === "undefined") return "light";
  const saved = window.localStorage.getItem(STORAGE_KEY);
  if (saved === "light" || saved === "dark") return saved;
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function readInitialFontScale(): FontScale {
  if (typeof window === "undefined") return "md";
  const saved = window.localStorage.getItem(FONT_KEY);
  if (saved === "sm" || saved === "md" || saved === "lg" || saved === "xl") return saved;
  return "md";
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<ColorMode>(readInitialMode);
  const [fontScale, setFontScaleState] = useState<FontScale>(readInitialFontScale);

  useEffect(() => {
    document.documentElement.dataset.theme = mode;
    document.documentElement.style.colorScheme = mode;
    window.localStorage.setItem(STORAGE_KEY, mode);
  }, [mode]);

  useEffect(() => {
    // 用 body.zoom 整体缩放 UI, 让所有 inline 写死的 px 也跟着字号设置走。
    // 现代浏览器(Chrome / Safari / Firefox 126+) 都支持 CSS zoom。
    const z = FONT_SCALE_ZOOM[fontScale];
    document.body.style.zoom = String(z);
    document.documentElement.dataset.fontScale = fontScale;
    window.localStorage.setItem(FONT_KEY, fontScale);
  }, [fontScale]);

  const value = useMemo<ThemeCtx>(
    () => ({
      mode,
      setMode: setModeState,
      toggle: () => setModeState((m) => (m === "dark" ? "light" : "dark")),
      fontScale,
      setFontScale: setFontScaleState,
    }),
    [mode, fontScale],
  );

  return (
    <Ctx.Provider value={value}>
      <ConfigProvider locale={zhCN} theme={makeAntdTheme(mode)}>
        {children}
      </ConfigProvider>
    </Ctx.Provider>
  );
}

export function useColorMode(): ThemeCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useColorMode must be inside ThemeProvider");
  return ctx;
}
